import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Chip, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { brand, gray } from "../../../../theme/themePrimitives";
import {
  MAX_LENGTH_DESCRIPTION,
  MAX_LENGTH_SHORT_LABEL,
} from "../../../../utils/inputLimits";

export type VersionField = "version" | "note";

interface VersionDetailsProps {
  version: string;
  note: string;
  onChange: (field: VersionField, value: string) => void;
  versionError?: string;
  noteError?: string;
  disabled?: boolean;
  title?: string;
  required?: boolean;
}

export const VersionDetails: React.FC<VersionDetailsProps> = ({
  version,
  note,
  onChange,
  versionError,
  noteError,
  disabled = false,
  title = "Version details",
  required = true,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: brand[50],
        border: `1px solid ${brand[100]}`,
        borderRadius: "12px",
        p: 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        {required && (
          <Chip
            label="Required"
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              borderRadius: "16px",
              borderColor: brand[400],

              backgroundColor: brand[50],
              "& .MuiChip-label": {
                px: 1,
                fontSize: 12,
                color: `${brand[700]} !important`,
              },
            }}
          />
        )}
      </Stack>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" color={gray[700]}>
            Version
          </Typography>
          <TextField
            fullWidth
            id="version"
            name="version"
            placeholder="Enter version"
            type="text"
            value={version}
            disabled={disabled}
            onChange={(e) => onChange("version", e.target.value)}
            variant="outlined"
            size="small"
            error={Boolean(versionError)}
            helperText={versionError}
            sx={{
              // Keep the white surface on the input only so the helper text
              // below it sits on the section background.
              "& .MuiOutlinedInput-root": {
                height: "44px",
                backgroundColor: "white",
                borderRadius: "8px",
              },
            }}
            slotProps={{
              input: {
                endAdornment: versionError && (
                  <ErrorOutlineIcon color="error" fontSize="small" />
                ),
              },
              htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
            }}
          />
        </Stack>

        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" color={gray[700]}>
            Note
          </Typography>
          <TextField
            fullWidth
            id="note"
            name="note"
            placeholder="Enter note"
            type="text"
            value={note}
            disabled={disabled}
            onChange={(e) => onChange("note", e.target.value)}
            variant="outlined"
            size="small"
            error={Boolean(noteError)}
            helperText={noteError}
            sx={{
              // Keep the white surface on the input only so the helper text
              // below it sits on the section background.
              "& .MuiOutlinedInput-root": {
                height: "44px",
                backgroundColor: "white",
                borderRadius: "8px",
              },
            }}
            slotProps={{
              input: {
                endAdornment: noteError && (
                  <ErrorOutlineIcon color="error" fontSize="small" />
                ),
              },
              htmlInput: { maxLength: MAX_LENGTH_DESCRIPTION },
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
};
